import React, { useEffect } from 'react';
import { Shield, Lock, FileCheck, AlertTriangle, CheckCircle, Zap, ArrowRight, Terminal } from 'lucide-react';
import { ParticleField } from '../components/ParticleField';
import { LogoMarquee } from '../components/LogoMarquee';
import { DemoForm } from '../components/DemoForm';
import { DashboardMockup } from '../components/DashboardMockup';

const services = [
  {
    icon: Shield,
    title: 'Vulnerability Assessment',
    copy: 'Advanced AI models scan your infrastructure to identify security gaps before attackers do. Real-time threat detection with actionable insights.',
  },
  {
    icon: Lock,
    title: 'Penetration Testing',
    copy: 'Simulated attacks using machine learning to test your defenses. Comprehensive reports with prioritized remediation steps.',
  },
  {
    icon: FileCheck,
    title: 'Compliance Monitoring',
    copy: 'Automated compliance checking against industry standards like GDPR, HIPAA, and PCI-DSS. Stay audit-ready with continuous monitoring.',
  },
  {
    icon: AlertTriangle,
    title: 'Threat Intelligence',
    copy: 'AI-powered threat detection that learns from global security patterns. Stay ahead of emerging threats with predictive analytics.',
  },
];

const benefits = [
  { icon: Zap, title: 'Fast & Efficient', copy: 'AI automation reduces testing time by 80% compared to traditional methods' },
  { icon: CheckCircle, title: 'Cost-Effective', copy: 'Enterprise-grade security at SME-friendly prices. No hidden fees.' },
  { icon: Shield, title: 'Proven Results', copy: 'Our AI models are trained on millions of security patterns and real-world threats' },
  { icon: Lock, title: 'Compliance Ready', copy: 'Meet industry standards and pass audits with confidence' },
];

const marqueeItems = ['GDPR', 'HIPAA', 'PCI-DSS', 'Vulnerability Assessment', 'Penetration Testing', 'Threat Intelligence', 'Continuous Monitoring'];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const LandingPage = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page" data-testid="landing-page">
      <header className="dark-header">
        <div className="dark-logo-text" data-testid="site-logo">
          <span className="mark">B</span>ountyplz
        </div>
        <nav className="dark-nav">
          <a href="#services" className="dark-nav-link" data-testid="nav-services">Services</a>
          <a href="#why-us" className="dark-nav-link" data-testid="nav-why-us">Why Us</a>
          <a href="#contact" className="dark-nav-link" data-testid="nav-contact">Contact</a>
        </nav>
      </header>

      {/* Hero */}
      <section className="hero-section" data-testid="hero-section">
        <ParticleField count={2200} />
        <div className="hero-glow" />
        <div className="hero-content">
          <div className="hero-badge" data-testid="hero-badge">
            <Terminal size={14} />
            <span>AI security testing built for SMEs</span>
          </div>
          <h1 className="display-huge" data-testid="hero-title">
            AI-Powered Security Testing<br />for <span className="accent">Modern Businesses</span>
          </h1>
          <p className="body-large hero-subtitle">
            Protect your digital assets with advanced machine learning. We help SMEs identify vulnerabilities, strengthen defenses, and ensure compliance—efficiently and cost-effectively.
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => scrollTo('contact')} data-testid="hero-get-started-button">
              Get Started
              <ArrowRight size={16} />
            </button>
            <button className="btn-secondary" onClick={() => scrollTo('services')} data-testid="hero-learn-more-button">
              Learn More
            </button>
          </div>
          <div className="hero-meta">
            <div className="hero-meta-item">
              <span className="value">80%</span>
              <span className="label">Faster testing</span>
            </div>
            <div className="hero-meta-item">
              <span className="value">24/7</span>
              <span className="label">Emergency response</span>
            </div>
            <div className="hero-meta-item">
              <span className="value">04</span>
              <span className="label">Core service lines</span>
            </div>
          </div>
          <LogoMarquee />
        </div>
      </section>

      {/* Marquee */}
      <div className="marquee" data-testid="marquee">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <React.Fragment key={dup}>
              {marqueeItems.map((item) => (
                <span key={`${dup}-${item}`}>
                  <em>/</em> {item}
                </span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Platform mockup */}
      <section className="platform-section" data-testid="platform-section">
        <div className="section-head reveal">
          <span className="mono-label is-alert">Continuous coverage</span>
          <h2 className="display-large">One console for every<br />exploitable risk.</h2>
          <p className="body-large">
            Findings land ranked by severity with validated impact and remediation context—so your team fixes what actually matters.
          </p>
        </div>
        <div className="reveal">
          <DashboardMockup />
        </div>
      </section>

      {/* Services */}
      <section id="services" data-testid="services-section">
        <div className="section-head reveal">
          <span className="mono-label">Our Services</span>
          <h2 className="display-large">Comprehensive security solutions powered by AI</h2>
        </div>
        <div className="service-grid">
          {services.map(({ icon: Icon, title, copy }, i) => (
            <div className="service-cell reveal" key={title} data-testid={`service-card-${i}`}>
              <div className="service-top">
                <span className="service-index">{String(i + 1).padStart(2, '0')}</span>
                <span className="service-icon"><Icon size={22} /></span>
              </div>
              <h3 className="heading-2">{title}</h3>
              <p className="body-medium">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section id="why-us" data-testid="why-us-section">
        <div className="section-head reveal">
          <span className="mono-label">Why Choose Bountyplz</span>
          <h2 className="display-large">Built for SMEs who take security seriously</h2>
        </div>
        <div className="why-grid">
          {benefits.map(({ icon: Icon, title, copy }, i) => (
            <div className="benefit-item reveal" key={title} data-testid={`benefit-item-${i}`}>
              <span className="benefit-icon"><Icon size={20} /></span>
              <div className="benefit-content">
                <h3 className="heading-3">{title}</h3>
                <p className="body-medium">{copy}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <div className="stats-strip" data-testid="stats-strip">
        <div className="stat-cell">
          <div className="stat-value">80<span>%</span></div>
          <span className="mono-label is-muted stat-label">Less testing time</span>
        </div>
        <div className="stat-cell">
          <div className="stat-value">24/<span>7</span></div>
          <span className="mono-label is-muted stat-label">Security emergencies</span>
        </div>
        <div className="stat-cell">
          <div className="stat-value">0<span> noise</span></div>
          <span className="mono-label is-muted stat-label">Prioritized findings only</span>
        </div>
      </div>

      {/* CTA */}
      <section id="contact" className="cta-section" data-testid="cta-section">
        <div className="cta-glow" />
        <div className="cta-layout">
          <div className="cta-content">
            <span className="mono-label">Get in touch</span>
            <h2 className="display-medium" style={{ marginTop: 22 }}>Ready to Secure Your Business?</h2>
            <p className="body-large">
              Join hundreds of SMEs who trust Bountyplz for their security testing needs.
            </p>
            <p className="body-medium" style={{ marginTop: 18 }}>
              Tell us what you want tested and we'll come back with a scoped demo — usually within one business day.
            </p>
          </div>
          <DemoForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="dark-footer" data-testid="site-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Bountyplz LLC</h3>
            <p className="body-small footer-description">
              AI-powered security testing for modern businesses. Protect what matters most.
            </p>
          </div>
          <div className="footer-section">
            <h4>Services</h4>
            <ul className="footer-links">
              {services.map(({ title }) => (
                <li key={title}><a href="#services" className="body-small">{title}</a></li>
              ))}
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact</h4>
            <ul className="footer-links">
              <li><a href="mailto:info@bearer.ai" className="body-small" data-testid="footer-email">info@bearer.ai</a></li>
              <li><span className="body-small">Available 24/7 for security emergencies</span></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="body-small">© 2025 Bountyplz LLC. All rights reserved.</p>
          <p className="mono-label is-muted">Autonomous security testing</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
